'use client';

import Icon from '@/components/ui/AppIcon';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import Navbar from '@/components/common/Navbar';

export default function TermsOfServicePage() {
  return (
    <main className="min-h-screen bg-background">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Terms of Service</h1>
            <p className="text-xl text-white/90">Last updated: February 19, 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-foreground mb-6">Agreement to Terms</h2>
              <p className="text-muted-foreground mb-6">
                By accessing and using the services of Gelle Cleaning LLC ("Company", "we", "our", or "us"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Services</h2>
              <p className="text-muted-foreground mb-6">
                Gelle Cleaning LLC provides professional residential and commercial cleaning services in the Twin Cities metropolitan area. Our services include but are not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Deep Cleaning</li>
                <li>Standard Cleaning</li>
                <li>Move-In/Move-Out Cleaning</li>
                <li>Office Cleaning</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Booking and Payment</h2>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Booking Process</h3>
              <p className="text-muted-foreground mb-6">
                All bookings must be made through our website or by contacting us directly. You must provide accurate and complete information when booking our services.
              </p>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Payment Terms</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Payment is required at the time of booking</li>
                <li>We accept major credit cards through our secure payment processor (Stripe)</li>
                <li>All prices are in USD and subject to applicable taxes</li>
                <li>Promotional codes and discounts must be applied at the time of booking</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Cancellation and Rescheduling</h2>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Cancellation Policy</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Cancellations made 24+ hours before scheduled service: Full refund</li>
                <li>Cancellations made less than 24 hours before service: 50% cancellation fee</li>
                <li>No-shows or same-day cancellations: No refund</li>
              </ul>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Rescheduling</h3>
              <p className="text-muted-foreground mb-6">
                You may reschedule your appointment through your customer portal or by contacting us. We request at least 24 hours notice for rescheduling.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Service Requirements</h2>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Access to Property</h3>
              <p className="text-muted-foreground mb-6">
                You must provide access to the property at the scheduled time. If our team cannot access the property, it will be considered a no-show and no refund will be provided.
              </p>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Property Condition</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>You must disclose any hazardous conditions or materials</li>
                <li>Excessive clutter or hoarding conditions may require additional fees</li>
                <li>We reserve the right to refuse service if conditions are unsafe</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Liability and Insurance</h2>
              <p className="text-muted-foreground mb-6">
                Gelle Cleaning LLC is fully insured and bonded. We take every precaution to protect your property. However:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>You must report any damage within 24 hours of service completion</li>
                <li>We are not liable for pre-existing damage or wear and tear</li>
                <li>We are not responsible for items left unsecured or not disclosed</li>
                <li>Our liability is limited to the cost of the service provided</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Satisfaction Guarantee</h2>
              <p className="text-muted-foreground mb-6">
                We stand behind the quality of our work. If you are not satisfied with our service:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Contact us within 24 hours of service completion</li>
                <li>We will return to address any issues at no additional charge</li>
                <li>If the issue cannot be resolved, we will provide a partial or full refund</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Intellectual Property</h2>
              <p className="text-muted-foreground mb-6">
                All content on our website, including text, graphics, logos, and images, is the property of Gelle Cleaning LLC and protected by copyright laws.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Privacy</h2>
              <p className="text-muted-foreground mb-6">
                Your use of our services is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Changes to Terms</h2>
              <p className="text-muted-foreground mb-6">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services constitutes acceptance of the modified terms.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Governing Law</h2>
              <p className="text-muted-foreground mb-6">
                These Terms of Service are governed by the laws of the State of Minnesota, United States, without regard to its conflict of law provisions.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Contact Information</h2>
              <p className="text-muted-foreground mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-green-50 p-6 rounded-lg">
                <p className="text-foreground mb-2"><strong>Email:</strong> legal@gellecleaning.com</p>
                <p className="text-foreground mb-2"><strong>Phone:</strong> (612) 555-1234</p>
                <p className="text-foreground"><strong>Address:</strong> Twin Cities, Minneapolis, MN</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}